// 👥 Employés E4 — mécanique d'édition par section : une seule section en édition, brouillon, garde « non enregistré ».
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DialogueModifsNonEnregistrees } from '@/components/employe/DialogueModifsNonEnregistrees';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const EditionCtx = createContext(null);

export function EditionFicheProvider({ employee, onSaved, onSaleChange, children }) {
  const [sectionActive, setSectionActive] = useState(null); // clé de la section en édition
  const [saleEtat, setSaleEtat] = useState(false);          // modifications non enregistrées
  const [enAttente, setEnAttente] = useState(null);         // E4b : action différée en attente du dialogue de l'app
  const sale = saleEtat;
  const setSale = useCallback((v) => { setSaleEtat(v); onSaleChange?.(v); }, [onSaleChange]);

  // E4b : `beforeunload` natif réservé à la fermeture/rechargement du navigateur.
  useEffect(() => {
    if (!sale) return undefined;
    const garde = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', garde);
    return () => window.removeEventListener('beforeunload', garde);
  }, [sale]);

  const demanderOuverture = useCallback((cle, apresOuverture) => {
    const ouvrir = () => { setSectionActive(cle); setSale(false); apresOuverture?.(); };
    if (sectionActive && sectionActive !== cle && sale) { setEnAttente(() => ouvrir); return false; }
    ouvrir(); return true;
  }, [sectionActive, sale, setSale]);

  const fermer = useCallback(() => { setSectionActive(null); setSale(false); }, [setSale]);

  /** Fermeture de la fiche : exécute `apresFermeture` tout de suite si rien n'est en suspens, sinon via le dialogue. */
  const demanderFermeture = useCallback((apresFermeture) => {
    if (!sale) { apresFermeture(); return true; }
    setEnAttente(() => () => { setSectionActive(null); setSale(false); apresFermeture(); });
    return false;
  }, [sale, setSale]);

  /** Changement d'onglet : édition « propre » → sortie du mode édition et navigation libre ; « sale » → dialogue (Abandonner = brouillon perdu + navigation). */
  const demanderNavigation = useCallback((apres) => {
    if (!sale) { setSectionActive(null); setSale(false); apres(); return true; }
    setEnAttente(() => () => { setSectionActive(null); setSale(false); apres(); });
    return false;
  }, [sale, setSale]);

  const value = useMemo(() => ({ employee, onSaved, sectionActive, sale, setSale, demanderOuverture, fermer, demanderFermeture, demanderNavigation }), [employee, onSaved, sectionActive, sale, setSale, demanderOuverture, fermer, demanderFermeture, demanderNavigation]);
  return (
    <EditionCtx.Provider value={value}>
      {children}
      <DialogueModifsNonEnregistrees open={!!enAttente} onContinuer={() => setEnAttente(null)} onAbandonner={() => { const a = enAttente; setEnAttente(null); a?.(); }} />
    </EditionCtx.Provider>
  );
}

export const useEditionFiche = () => useContext(EditionCtx);

/** Hook d'une section : brouillon limité aux champs de la section, PUT partiel, annulation, erreurs par champ. */
export function useEditionSection(cle, champs, { preparer } = {}) {
  const ctx = useEditionFiche();
  const { employee, onSaved, sectionActive, setSale, demanderOuverture, fermer } = ctx;
  const enEdition = sectionActive === cle;
  const [brouillon, setBrouillon] = useState({});
  const [erreurs, setErreurs] = useState({});
  const [saving, setSaving] = useState(false);

  const extraire = useCallback(() => Object.fromEntries(champs.map((c) => [c, employee?.[c] ?? null])), [champs, employee]);

  const modifier = () => { demanderOuverture(cle, () => { setBrouillon(extraire()); setErreurs({}); }); };
  const annuler = () => { setBrouillon({}); setErreurs({}); fermer(); };
  const changer = (champ, valeur) => { setBrouillon((b) => ({ ...b, [champ]: valeur })); setSale(true); setErreurs((e) => ({ ...e, [champ]: undefined })); };

  const enregistrer = async () => {
    const payload = {};
    champs.forEach((c) => { if (JSON.stringify(brouillon[c]) !== JSON.stringify(employee?.[c] ?? null)) payload[c] = brouillon[c]; });
    const final = preparer ? preparer(payload, brouillon) : payload;
    if (Object.keys(final).length === 0) { annuler(); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/employees/${employee.id}`, final, { headers: headers() });
      toast.success('Section enregistrée');
      (data.avertissements || []).forEach((a) => toast.warning(a, { duration: 10000 }));
      onSaved?.(data.employee || { ...employee, ...final });
      annuler();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const texte = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(' ; ') : "Erreur lors de l'enregistrement";
      const champ = (e.response?.status === 409 && /numéro d'employé/i.test(texte)) ? 'numero_employe' : null;
      if (champ) setErreurs((er) => ({ ...er, [champ]: texte })); else toast.error(texte);
    } finally { setSaving(false); }
  };

  const valeur = (c) => (enEdition ? brouillon[c] : employee?.[c]);
  return { enEdition, brouillon, erreurs, saving, modifier, annuler, changer, enregistrer, valeur, sale: ctx.sale };
}
